import * as React from 'react'
import {
    UNSAFE_LocationContext,
    UNSAFE_NavigationContext,
    useLocation,
} from 'react-router-dom'
import SkipNav from 'app/shared/layouts/SkipNav'
import Header from 'app/shared/layouts/Header'
import Footer from 'app/shared/layouts/Footer'
import useBasePage from 'app/shared/hooks/base-page.hook'
import Share from '../Share'
import { History } from 'history'
import { useBackHandle } from 'app/shared/hooks/util/backHandle.hook'
import {
    useDialog,
    useProgressDialog,
} from 'app/shared/context/CommonUIProvider'
import { useCommonCode } from 'app/shared/hooks/util/commonCode.hook'
import { urlList } from 'app/core/models/api/com/com0102'

interface IProps {
    children: React.ReactNode
    footerPosition?: string // 고정영역 형태 클래스 추가
    footerContent?: React.ReactNode // 하단 고정영역 안 컨텐츠
    bottomContent?: React.ReactNode // 하단 컨텐츠
    className?: string // 컨텐츠 클래스
    header?: boolean // 헤더
    headerClassName?: string // 헤더 클래스
    title?: React.ReactNode // 헤더 타이틀
    menu?: boolean // 전체메뉴 버튼 유무
    prev?: boolean // 뒤로가기 버튼 유무
    onPrev?: () => void // 뒤로가기 이벤트
    share?: string // 공유하기 링크주소, 값이 있으면 공유하기 버튼이 보여진다.
    product?: boolean // 상품몰가기 버튼 유무
    productAlert?: boolean
    chatbot?: boolean // 챗봇 버튼
}

export const BasePage: React.FC<IProps> = ({
    children,
    className,
    footerPosition,
    footerContent,
    bottomContent,
    title,
    menu,
    share = '',
    product,
    productAlert,
    chatbot,
    prev = true,
    onPrev,
    header = true,
    headerClassName,
}) => {
    const location = useLocation()
    const router = useBasePage()
    const handleBack = () => {
        router.goBack()
    }

    const dsrnTgtBacd = (() => {
        if (chatbot && share.length > 0) {
            return '110:KAKAO, 110:EXTL_LURL'
        }
        if (chatbot) {
            return '110:EXTL_LURL,'
        }

        if (share.length > 0) {
            return '110:KAKAO,'
        }

        return ''
    })()

    const { data: commonCode } = useCommonCode<{
        data: {
            '110:KAKAO': urlList[]
            '110:EXTL_LURL': urlList[]
        }
    }>({
        dsrnTgtBacd: dsrnTgtBacd,
        ccddId: '',
        cdgpId: '',
        fetchOnLoad: dsrnTgtBacd !== '',
    }) // 001: 공지알림 / 003: 배너 / 008:추천상품

    /**
     * 공유하기
     */
    const [showShare, setShowShare] = React.useState(false)
    const handleShare = (open: boolean) => {
        setShowShare(open)
    }
    const dialog = useDialog()
    const progressDialog = useProgressDialog()

    React.useEffect(() => {
        dialog.hide()
        // progressDialog.hide()
        window.scrollTo(0, 0)
        if (header) return
        document.getElementById('root').removeAttribute('style')
    }, [location.pathname, header])

    // 하단 도움말 선택시 하단으로 스크롤이동
    React.useEffect(() => {
        const helpHandle = (e) => {
            const target = e.target.closest('.p-accordion-header-link')
            if (!target) return
            if (target.closest('.accordion-help')) {
                setTimeout(() => {
                    const body =
                        document.scrollingElement || document.documentElement
                    const top = body.scrollHeight
                    window.scrollTo({ top, behavior: 'smooth' })
                }, 400) // 아코디언이 열리는 시간 후 실행
            }
        }
        bottomContent && document.addEventListener('click', helpHandle)
        return () => document.removeEventListener('click', helpHandle)
    }, [])

    const rootUrl = window.location.origin

    const routerContext = React.useContext(UNSAFE_NavigationContext)
    const locationContext = React.useContext(UNSAFE_LocationContext)
    const pName = locationContext.location.pathname
    const pState = locationContext.location.state
    /** 페이지에서 보안키패드 사용 시 떠있는데 뒤로가기하면 보안키패드가 유지됨 뒤로가기 잡아 처리 */
    React.useEffect(() => {
        const unListen = (routerContext.navigator as History).listen(
            ({ action }) => {
                if (action === 'POP') {
                    if (globalThis.mtk?.now) {
                        routerContext.navigator.push(pName, pState)
                        globalThis.mtk.close()
                    }
                }
            }
        )

        return () => {
            unListen()
        }
    }, [])

    return (
        <>
            <SkipNav />
            {header && (
                <Header
                    className={headerClassName}
                    prev={prev}
                    onPrev={onPrev ?? handleBack}
                    menu={menu}
                    chatbot={chatbot}
                    chatBotUrl={
                        chatbot
                            ? commonCode?.data['110:EXTL_LURL']?.find(
                                  (x) => x.ccddId === 'LINK_004'
                              )?.cdAtrbEtc4Con
                            : ''
                    }
                    share={share.length > 0}
                    onShare={handleShare}
                    product={product}
                    productAlert={productAlert}
                    title={title}
                />
            )}
            <div id="content" className={className} tabIndex={-1}>
                {children}
                <Share
                    visible={showShare}
                    onShare={handleShare}
                    shareLink={rootUrl + share}
                    kakaoKey={
                        commonCode?.data['110:KAKAO']?.find(
                            (x) => x.ccddId === 'API_KEY'
                        )?.cdAtrbEtc2Con
                    }
                />
            </div>
            {(footerContent || bottomContent) && (
                <Footer
                    className={footerPosition}
                    bottomContent={bottomContent}
                >
                    {footerContent}
                </Footer>
            )}
        </>
    )
}
